import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { dateIt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload } from "lucide-react";

const CHUNK = 4 * 1024 * 1024;

export function ImportPrezzarioDialog({ onClose }) {
  const qc = useQueryClient();
  const [anno, setAnno] = useState(String(new Date().getFullYear()));
  const [file, setFile] = useState(null);
  const [prog, setProg] = useState(null);
  const { data: annualita = [] } = useQuery({ queryKey: ["prezzario-annualita"], queryFn: async () => (await api.get("/prezzario/annualita")).data });
  const esiste = annualita.some((a) => a.annualita === anno);
  const carica = async () => {
    if (esiste && !window.confirm(`L'annualità ${anno} esiste già (${annualita.find((a) => a.annualita === anno)?.voci} voci). Sostituirla con il nuovo file?`)) return;
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const n = Math.ceil(file.size / CHUNK);
    try {
      for (let i = 0; i < n; i++) {
        const fd = new FormData();
        fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), "part");
        await api.post("/prezzario/import/chunk", fd, { params: { upload_id: uploadId, index: i } });
        setProg(Math.round(((i + 1) / n) * 90));
      }
      setProg(95);
      const d = (await api.post("/prezzario/import/finalize", null, { params: { upload_id: uploadId, annualita: anno, nome_file: file.name } })).data;
      setProg(100);
      qc.invalidateQueries({ queryKey: ["prezzario-annualita"] });
      toast.success(`Prezzario ${anno} importato: ${d.voci} voci`);
      onClose();
    } catch (e) {
      setProg(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Caricamento fallito");
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && prog === null && onClose()}>
      <DialogContent className="max-w-lg" data-testid="import-prezzario-dialog">
        <DialogHeader><DialogTitle className="font-display">Aggiorna Prezzario Regione Piemonte</DialogTitle><DialogDescription>Carica il file CSV "Listino" pubblicato dalla Regione per la nuova annualità. Il prezzario resta di sola lettura; i preventivi esistenti conservano i prezzi già inseriti.</DialogDescription></DialogHeader>
        <div className="rounded-md border border-slate-200 divide-y divide-slate-100 text-sm" data-testid="prezzario-annualita-lista">
          {annualita.length === 0 && <div className="p-3 text-slate-400">Nessuna annualità importata.</div>}
          {annualita.map((a) => <div key={a.annualita} className="flex justify-between px-3 py-1.5"><span className="font-semibold">{a.annualita}</span><span className="text-slate-500">{a.voci.toLocaleString("it-IT")} voci · {a.file} · {dateIt(a.data)}</span></div>)}
        </div>
        <div className="grid grid-cols-[110px_1fr] gap-3">
          <div className="space-y-1.5"><Label>Annualità</Label><Input value={anno} maxLength={4} onChange={(e) => setAnno(e.target.value.replace(/\D/g, ""))} data-testid="import-prezzario-anno" /></div>
          <div className="space-y-1.5"><Label>File CSV</Label><Input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-prezzario-file" /></div>
        </div>
        {prog !== null && <div className="h-2 rounded bg-slate-100 overflow-hidden" data-testid="import-prezzario-progress"><div className="h-full bg-primary transition-[width]" style={{ width: `${prog}%` }} /></div>}
        <DialogFooter><Button variant="outline" onClick={onClose} disabled={prog !== null}>Annulla</Button><Button onClick={carica} disabled={!file || anno.length !== 4 || prog !== null} data-testid="import-prezzario-avvia"><Upload className="h-4 w-4 mr-1.5" /> {prog !== null ? `Caricamento ${prog}%` : "Importa"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
